import { useCallback, useLayoutEffect, useRef } from 'react'

/**
 * keeps an anchor element visually fixed across a layout change that resizes
 * content above it. call capture() synchronously right before the state update
 * that triggers the resize; after the DOM commits, the window is scrolled by the
 * anchor's top delta so the anchor returns to where it was — no visible jump.
 *
 * only the render following a capture() is compensated, so unrelated reflows
 * (e.g. async data settling) are left alone.
 */
export const useScrollAnchor = <T extends HTMLElement>() => {
  const anchorRef = useRef<T>(null)
  const savedTop = useRef<number | null>(null)

  const capture = useCallback(() => {
    if (anchorRef.current) savedTop.current = anchorRef.current.getBoundingClientRect().top
  }, [])

  useLayoutEffect(() => {
    if (savedTop.current === null || !anchorRef.current) return
    const delta = anchorRef.current.getBoundingClientRect().top - savedTop.current
    savedTop.current = null
    if (delta !== 0) window.scrollBy(0, delta)
  })

  return { anchorRef, capture }
}
